import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CustomRouter } from 'src/admin-front/ajs-upgraded-providers';
import { EngagementService } from 'src/app/care/engagement/engagement.service';
import {
  KakaotalkTemplate,
  KakaotalkTemplateButtonTypeEnum,
} from 'src/app/parameters/message-templates/kakaotalk-templates/shared';
import { WhatsappTemplateButtonTypeEnum } from 'src/app/parameters/message-templates/whatsapp-templates/shared/models/whatsapp-template-button-type.enum';
import { WhatsappTemplate } from 'src/app/task-process/shared/models';
import { TaskProcessContext } from 'src/app/task-process/shared/task-process-context.class';
import { TaskProcessService } from 'src/app/task-process/shared/task-process.service';
import { environment } from 'src/environments/environment';
import { SiteTypeEnum } from '../../enums';
import { SiteV2 } from '../../models';
import { BasicService, RoutePaths } from '../../services/basic.service';
import { ChooseTemplateFlowService } from './shared/choose-template-flow.service';
import { EngagementTemplateFlowStepEnum } from './shared/engagement-template-flow-step.enum';

type EngagementTemplate = WhatsappTemplate | KakaotalkTemplate;

@Component({
  selector: 'app-choose-template-flow',
  templateUrl: './choose-template-flow.component.html',
  styleUrls: ['./choose-template-flow.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ChooseTemplateFlowComponent implements OnInit, OnDestroy {
  @Input() taskProcessContext: TaskProcessContext;

  private _templates: EngagementTemplate[];
  @Input()
  set templates(value: EngagementTemplate[]) {
    this._templates = value;
    this.initTemplates();
  }
  get templates(): EngagementTemplate[] {
    return this._templates;
  }

  @Output() tryToSearchConversation: EventEmitter<{ userExternalId: number; siteId: number }> = new EventEmitter();
  @Output() getTemplatesForAccount: EventEmitter<SiteV2> = new EventEmitter();

  set selectedTemplate(value) {
    this.chooseTemplateFlowService.selectedTemplate = value;
  }
  get selectedTemplate(): any {
    return this.chooseTemplateFlowService.selectedTemplate;
  }
  EngagementTemplateFlowStepEnum = EngagementTemplateFlowStepEnum;
  SiteTypeEnum = SiteTypeEnum;

  tooltipBoundariesSelector = environment.bundledWithAngularJSApp ? 'angular-task-process-page' : 'app-task-process';
  private subscriptions: Subscription = new Subscription();

  constructor(
    public chooseTemplateFlowService: ChooseTemplateFlowService,
    public engagementService: EngagementService,
    private taskProcessService: TaskProcessService,
    private basicService: BasicService,
    private customRouter: CustomRouter
  ) {
    this.subscriptions.add(
      chooseTemplateFlowService.clearBS.subscribe(value => {
        if (value) {
          this.clear();
        }
      })
    );
  }

  ngOnInit() {
    this.chooseTemplateFlowService.taskProcessContext = this.taskProcessContext;
    this.chooseTemplateFlowService.selectedTemplate = this.selectedTemplate;
  }

  initTemplates() {
    switch (this.taskProcessContext?.engagement ? this.engagementService.channelType : SiteTypeEnum.WhatsApp) {
      case SiteTypeEnum.WhatsApp:
        this.initWhatsappTemplates();
        break;

      case SiteTypeEnum.Kakaotalk:
        this.initKakaotalkTemplates();
        break;
    }
  }

  /**
   * builds the parameters property for all templates
   */
  private initWhatsappTemplates() {
    (this.templates || []).forEach((template: WhatsappTemplate) => {
      template.parameters = [];
      for (const key of Object.keys(template.placeholders || {})) {
        const index = Number(key.match(/{{([0-9]+)}}/)[1]) - 1; // placeholders start at {{1}}
        template.parameters[index] = {
          placeholder: template.placeholders[key]?.label || (template.placeholders[key] as any),
          value: '',
        };
      }
      template.textWithReplacedPlaceholders = this.getPreview(template, true);
      template.urlButtons = (template.buttons || [])
        .filter(
          ({ type, url, example }) =>
            type === WhatsappTemplateButtonTypeEnum.URL && url.includes('{{1}}') && example?.length
        )
        .map(urlButton => ({ ...urlButton, example: [''], url: urlButton.url.replace('{{1}}', '') }));
      template.quickReplyButtons = (template.buttons || []).filter(
        ({ type }) => type === WhatsappTemplateButtonTypeEnum.QUICK_REPLY
      );
      template.copyCodeButtons = (template.buttons || []).filter(
        ({ type }) => type === WhatsappTemplateButtonTypeEnum.COPY_CODE
      );
    });
  }

  /**
   * builds the parameters property for all kakaotalk templates
   */
  private initKakaotalkTemplates() {
    (this.templates || []).forEach((template: KakaotalkTemplate) => {
      template.parameters = [];
      for (const key of Object.keys(template.placeholders || {})) {
        const index = Number(key.match(/{{([0-9]+)}}/)[1]) - 1; // placeholders start at {{1}}
        template.parameters[index] = {
          placeholder: template.placeholders[key]?.label || template.placeholders[key],
          value: '',
        };
      }
      template.textWithReplacedPlaceholders = this.getPreview(template, true);
      template.urlButtons = (template.buttons || [])
        .filter(
          ({ type, url, example }) =>
            type === KakaotalkTemplateButtonTypeEnum.URL && url.includes('{{1}}') && example?.length
        )
        .map(urlButton => ({ ...urlButton, example: [''], url: urlButton.url, urlFormatted: urlButton.url }));
    });
  }

  private getPreview(template: any, forcePlaceholder: boolean): string {
    let result = template.text || template.body;
    let index = 1;
    for (const parameter of template.parameters) {
      const placeholder = `<${parameter.placeholder?.label || parameter.placeholder}>`;
      const replacement = forcePlaceholder ? placeholder : parameter.value || placeholder;
      result = result.replace(`{{${index++}}}`, replacement);
    }
    return result;
  }

  private clear() {
    this.initTemplates();
  }

  isValid() {
    if (!this.selectedTemplate) {
      return false;
    }
    let hasHeaderValid = true;
    if (this.selectedTemplate.header) {
      if (this.selectedTemplate.header.type === 'text') {
        hasHeaderValid = !!this.selectedTemplate.header.text;
      } else {
        const { media_url } = this.selectedTemplate.header;
        hasHeaderValid = !!media_url;
      }
    }
    return hasHeaderValid && this.selectedTemplate.parameters.every(parameter => !!parameter.value);
  }

  sendNow() {
    const userExternalId = this.engagementService.strategy.userExternalId;
    const siteId = this.taskProcessContext.site.id;
    const { optionsFromCRM } = this.engagementService.strategy;
    let crm = undefined;
    if (optionsFromCRM) {
      try {
        crm = JSON.parse(atob(optionsFromCRM));
      } catch {
        crm = undefined;
      }
    }
    this.basicService.showSpinner();
    const messageData =
      this.chooseTemplateFlowService.messageData ||
      this.chooseTemplateFlowService.extractDataFromTemplate(this.selectedTemplate);
    this.taskProcessService
      .sendMessageEngagement(
        userExternalId,
        siteId,
        messageData,
        crm,
        this.engagementService.channelType,
        this.engagementService.strategy.userIdType
      )
      .pipe(finalize(() => this.basicService.hideSpinner()))
      .subscribe(() => {
        this.engagementService.flowStep = EngagementTemplateFlowStepEnum.CHOOSE_TEMPLATE_MESSAGE;
        this.engagementService.strategy.showComposerBS.next(false);
        let externalUserIds: any = userExternalId;
        if (this.engagementService.channelType === SiteTypeEnum.Kakaotalk) {
          externalUserIds = this.engagementService.computeUserExternalIds(userExternalId);
        }
        if (this.engagementService.strategy.isCRMMode) {
          this.basicService.navigateRoot(RoutePaths.CulDeSac, { skipLocationChange: true }).then(() =>
            this.basicService.navigateRoot(
              `/routes/auth/engagement/send-message/sites/${siteId}/user-external-id/${externalUserIds}`,
              {
                queryParams: {
                  crm: btoa(JSON.stringify(crm)),
                  from: this.taskProcessContext.from ? btoa(JSON.stringify(this.taskProcessContext.from)) : null,
                  externalUser: this.engagementService.strategy.externalUser,
                },
              }
            )
          );
        } else {
          this.customRouter.go(
            'auth.alcmeon.engagement.send-message.user-external-id',
            {
              siteId,
              externalUserIds,
            },
            { reload: true, inherit: true, notify: true }
          );
        }
      });
  }
  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
